// ============================================================================
// hardwareGrade.ts — "Grade your own system" browser probe + tier estimate.
// ============================================================================
// Mirrors the device-side grader (kinsafe_local/hardware.py) closely enough to
// give a prospect an HONEST tier + expected per-frame latency BEFORE they
// install anything (PRD §1.9-B, ROADMAP D5). Zero abuse surface: it only reads
// what the browser already exposes — no upload, no network, no fingerprint sent
// anywhere. Everything below runs locally in the prospect's tab.
//
// The browser CANNOT see everything hardware.py sees (it has no PowerShell
// Win32_VideoController, no psutil, no AVX2 flag). So this is a best-effort,
// clearly-labeled ESTIMATE; the downloadable grader.exe (affordance only, not
// built) reuses the real hardware.py for an exact grade. We mirror the SAME
// tier names and the SAME grading order so the estimate lines up with the real
// thing whenever the browser can supply the inputs.
// ============================================================================

export type Tier = "lite" | "standard" | "max";

export type GpuKind = "dgpu" | "igpu" | "none";

export interface BrowserProfile {
  // Raw WebGL UNMASKED_RENDERER_WEBGL string (e.g. "ANGLE (NVIDIA GeForce
  // RTX 3060 ...)"), or null if unavailable / blocked.
  gpuRenderer: string | null;
  gpuKind: GpuKind;
  // Whether the renderer string looks like a usable discrete accelerator
  // (the browser can't read CUDA/DirectML availability, so we infer from name).
  hasDiscreteGpu: boolean;
  // navigator.deviceMemory — coarse RAM in GB (Chrome only; capped at 8 by the
  // spec, so "8" means ">= 8"). null on Firefox/Safari.
  deviceMemoryGb: number | null;
  // navigator.hardwareConcurrency — logical cores. Usually present.
  logicalCores: number | null;
}

// ---- GPU name classification (port of hardware.py _classify_gpu, adapted to
//      the WebGL renderer string format) -------------------------------------
export function classifyGpu(renderer: string | null): GpuKind {
  if (!renderer) return "none";
  const n = renderer.toLowerCase();

  // Discrete first — these win even though "amd"/"intel" also match integrated.
  if (
    n.includes("nvidia") ||
    n.includes("geforce") ||
    n.includes("rtx") ||
    n.includes("gtx") ||
    n.includes("quadro") ||
    n.includes("tesla")
  ) {
    return "dgpu";
  }
  if (n.includes("intel") && n.includes("arc")) return "dgpu"; // Intel Arc = discrete
  if (
    n.includes("radeon rx") ||
    n.includes("radeon pro") ||
    n.includes("radeon vii")
  ) {
    return "dgpu";
  }

  // Integrated.
  if (n.includes("iris") || n.includes("uhd") || n.includes("hd graphics")) {
    return "igpu";
  }
  if (n.includes("vega") && !n.includes("radeon rx")) return "igpu";
  if (n.includes("radeon")) return "igpu"; // bare Radeon → integrated (safe)
  return "none";
}

// ---- Read the browser's hardware signals (never throws) -------------------
export function probeBrowser(): BrowserProfile {
  let gpuRenderer: string | null = null;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      (canvas.getContext("webgl") as WebGLRenderingContext | null) ??
      (canvas.getContext(
        "experimental-webgl",
      ) as WebGLRenderingContext | null);
    if (gl) {
      const ext = gl.getExtension("WEBGL_debug_renderer_info");
      if (ext) {
        gpuRenderer = gl.getParameter(
          ext.UNMASKED_RENDERER_WEBGL,
        ) as string;
      } else {
        // Some browsers gate the unmasked string; fall back to the masked one.
        gpuRenderer = gl.getParameter(gl.RENDERER) as string;
      }
    }
  } catch {
    gpuRenderer = null;
  }

  const gpuKind = classifyGpu(gpuRenderer);

  // navigator.deviceMemory / hardwareConcurrency are not in the lib DOM types
  // everywhere — read defensively.
  const nav = navigator as Navigator & { deviceMemory?: number };
  const deviceMemoryGb =
    typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;
  const logicalCores =
    typeof navigator.hardwareConcurrency === "number"
      ? navigator.hardwareConcurrency
      : null;

  return {
    gpuRenderer,
    gpuKind,
    hasDiscreteGpu: gpuKind === "dgpu",
    deviceMemoryGb,
    logicalCores,
  };
}

// ---- grade(profile) — port of hardware.py grade(), adapted to browser inputs.
// Treats unknowns charitably (same as the Python: never demote on a value we
// could not measure). Because the browser can't read CUDA/DirectML, a discrete
// GPU name is taken as "usable acceleration" for the MAX bar.
export interface GradeResult {
  tier: Tier;
  // The single human-readable reason the tier was chosen (for the UI).
  reason: string;
  // True when key inputs were unreadable, so the UI can say "estimate — run the
  // grader for an exact result".
  lowConfidence: boolean;
}

export function gradeBrowser(p: BrowserProfile): GradeResult {
  const ram = p.deviceMemoryGb;
  const cores = p.logicalCores;
  const lowConfidence = ram === null || p.gpuRenderer === null;

  // --- MAX: usable dGPU + headroom. The browser can't see VRAM, so we use the
  //     discrete-GPU name + (deviceMemory >= 8, the spec's cap) as the proxy for
  //     the Python "vram>=6 AND ram>=16" bar. Clearly an estimate.
  if (p.hasDiscreteGpu && (ram === null || ram >= 8)) {
    return {
      tier: "max",
      reason:
        "A dedicated GPU was detected — the accurate describer runs fast and this PC could even serve other machines on your network.",
      lowConfidence,
    };
  }

  // --- LITE: any weak-box signal we actually measured (mirrors the Python
  //     ram<8 / <=2-core triggers; AVX2 + clock aren't browser-readable).
  let weak = false;
  let weakReason = "";
  if (ram !== null && ram < 8) {
    weak = true;
    weakReason = "Limited memory was detected";
  }
  if (cores !== null && cores <= 2) {
    weak = true;
    weakReason = "Only a couple of CPU cores were detected";
  }
  if (weak) {
    return {
      tier: "lite",
      reason: `${weakReason} — KinSafe runs in a light footprint, leaning on the fast gatekeeper and describing flagged frames more slowly.`,
      lowConfidence,
    };
  }

  // --- STANDARD: the mainstream middle.
  return {
    tier: "standard",
    reason:
      "A mainstream multi-core CPU with integrated or no dedicated graphics — the recommended balance: fast gatekeeper, accurate 2B describer.",
    lowConfidence,
  };
}
